import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import LinearProgress from "@mui/material/LinearProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import CheckIcon from "@mui/icons-material/Check";
import EastIcon from "@mui/icons-material/East";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { formatPhoneNumber } from "../../utils/services";
import { API_CONSTANTS, getSpectoraImportApiKey } from "../../config/api-constants";
import "./DataImport.scss";

// ── Mapping tokens ─────────────────────────────────────────────────────────────

const MAPPING_TOKENS = {
  // __skip__ intentionally omitted — handled via two-zone UI, not a dropdown option
  __split_name__: "Split → First Name + Last Name",
  __split_client_name__: "Split → Client First + Last Name",
  __split_ba_name__: "Split → Buyer Agent First + Last Name",
  __parse_address__: "Parse → Address, City, State, Zip",
  __notes__: "Append → Notes field",
};

// Normalize a column name for fuzzy matching: lowercase, remove non-alphanumeric
function normalizeColName(name) {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function getDefaultSplitToken(config = {}) {
  if (config.id === "clients") return "__split_client_name__";
  if (config.id === "agents") return "__split_ba_name__";
  return "__split_name__";
}

function isCombinedNameColumn(colNorm, colLower) {
  const combinedPatterns = new Set([
    "firstnameandlastname",
    "firstandlastname",
    "firstlastname",
    "fullname",
    "clientname",
    "customername",
    "contactname",
    "agentname",
    "buyeragentname",
    "selleragentname",
    "name",
  ]);
  if (combinedPatterns.has(colNorm)) return true;
  if (colNorm.includes("firstname") && colNorm.includes("lastname")) return true;
  if (colLower.includes("first name") && colLower.includes("last name")) return true;
  return false;
}

function isSplittableMappingToken(target) {
  return (
    target === "__split_name__" ||
    target === "__split_client_name__" ||
    target === "__split_ba_name__"
  );
}

// Infer the best mapping target from a source column name and step config
function inferMappingTarget(sourceCol, config = {}) {
  const autoSuggest = config.autoSuggest ?? {};
  const importFields = config.importFields ?? [];
  const colLower = sourceCol.toLowerCase().trim();
  const colNorm = normalizeColName(sourceCol);
  const autoEntries = Object.entries(autoSuggest);

  if (autoSuggest[sourceCol] !== undefined) return autoSuggest[sourceCol];

  const ciMatch = autoEntries.find(([pattern]) => pattern.toLowerCase() === colLower);
  if (ciMatch) return ciMatch[1];

  const fuzzyAutoMatch = autoEntries.find(([pattern]) => normalizeColName(pattern) === colNorm);
  if (fuzzyAutoMatch) return fuzzyAutoMatch[1];

  const importFieldMatch = importFields.find((field) => normalizeColName(field) === colNorm);
  if (importFieldMatch) return importFieldMatch;

  if (isCombinedNameColumn(colNorm, colLower)) {
    return getDefaultSplitToken(config);
  }

  return "__skip__";
}

function getRestorableMappingTarget(sourceCol, config = {}) {
  const inferred = inferMappingTarget(sourceCol, config);
  if (inferred && inferred !== "__skip__") return inferred;
  return config.importFields?.[0] ?? getDefaultSplitToken(config);
}

function canRestoreSkippedColumn(sourceCol, config = {}) {
  const target = getRestorableMappingTarget(sourceCol, config);
  if (isSplittableMappingToken(target)) return true;
  return (config.importFields ?? []).includes(target);
}

// Build initial per-column mapping using autoSuggest, import field names, and name-split detection
function buildInitialMapping(headers, config, rows = []) {
  const result = {};
  headers.forEach((col) => {
    result[col] = inferMappingTarget(col, config);
    if (result[col] !== "__skip__" && rows.length > 0) {
      const hasData = rows.some((row) => String(row[col] ?? "").trim() !== "");
      if (!hasData) result[col] = "__skip__";
    }
  });
  return result;
}

// Transform uploaded rows using a confirmed mapping
function getSplitNameTargetFields(token, config = {}) {
  if (token === "__split_name__") {
    return ["First Name", "Last Name"];
  }
  if (token === "__split_client_name__") {
    return config.id === "agents"
      ? ["BA_FIRST_NAME", "BA_LAST_NAME"]
      : ["CLIENT_FIRST_NAME", "CLIENT_LAST_NAME"];
  }
  if (token === "__split_ba_name__") {
    return config.id === "clients"
      ? ["CLIENT_FIRST_NAME", "CLIENT_LAST_NAME"]
      : ["BA_FIRST_NAME", "BA_LAST_NAME"];
  }
  return [];
}

function getMappingTokensForConfig(config) {
  const tokens = {
    __split_name__: MAPPING_TOKENS.__split_name__,
    __parse_address__: MAPPING_TOKENS.__parse_address__,
    __notes__: MAPPING_TOKENS.__notes__,
  };
  if (config.id === "clients") {
    tokens.__split_client_name__ = MAPPING_TOKENS.__split_client_name__;
  } else if (config.id === "agents") {
    tokens.__split_ba_name__ = MAPPING_TOKENS.__split_ba_name__;
  } else if (config.id === "history") {
    tokens.__split_client_name__ = MAPPING_TOKENS.__split_client_name__;
    tokens.__split_ba_name__ = MAPPING_TOKENS.__split_ba_name__;
  }
  return tokens;
}

function applySplitNameValue(out, val, firstField, lastField) {
  const [first = "", ...rest] = String(val ?? "").trim().split(/\s+/);
  out[firstField] = out[firstField] || first;
  out[lastField] = out[lastField] || rest.join(" ");
}

function applyMapping(rows, mapping, config = {}) {
  const mapped = rows.map((row) => {
    const out = {};
    for (const [src, target] of Object.entries(mapping)) {
      const rawVal = row[src] ?? "";
      const val = String(rawVal).trim();
      if (target === "__skip__") continue;
      if (target === "__split_name__") {
        applySplitNameValue(out, val, "First Name", "Last Name");
      } else if (target === "__parse_address__") {
        const parts = val.split(",").map((s) => s.trim());
        out["Address 1"] = parts[0] ?? "";
        out["City"] = parts[1] ?? "";
        const stateZip = (parts[2] ?? "").trim().split(/\s+/);
        out["State Abbreviation"] = stateZip[0] ?? "";
        out["Zip"] = stateZip[1] ?? "";
      } else if (target === "__split_client_name__") {
        const [firstField, lastField] = getSplitNameTargetFields("__split_client_name__", config);
        applySplitNameValue(out, val, firstField, lastField);
      } else if (target === "__split_ba_name__") {
        const [firstField, lastField] = getSplitNameTargetFields("__split_ba_name__", config);
        applySplitNameValue(out, val, firstField, lastField);
      } else if (target === "__notes__") {
        out["Notes"] = [out["Notes"], val].filter(Boolean).join("; ");
      } else {
        out[target] = String(formatCellValue(target, rawVal)).trim();
      }
    }
    return out;
  }).filter((row) => !isRowEmpty(row));

  if (config.appendColumns) {
    return mapped.map((row) => ({ ...row, ...config.appendColumns }));
  }
  return mapped;
}

function isRowEmpty(row) {
  if (!row || typeof row !== "object") return true;
  return !Object.values(row).some((val) => String(val ?? "").trim() !== "");
}

function filterNonEmptyRows(rows) {
  return (rows ?? []).filter((row) => !isRowEmpty(row));
}

function getMappedImportFields(mapping, config = {}) {
  const covered = new Set();
  Object.values(mapping ?? {}).forEach((target) => {
    if (!target || target === "__skip__") return;
    if (
      target === "__split_name__" ||
      target === "__split_client_name__" ||
      target === "__split_ba_name__"
    ) {
      getSplitNameTargetFields(target, config).forEach((field) => covered.add(field));
      return;
    }
    if (target === "__parse_address__") {
      ["Address 1", "City", "State Abbreviation", "Zip"].forEach((field) => covered.add(field));
      return;
    }
    if (target === "__notes__") {
      covered.add("Notes");
      return;
    }
    covered.add(target);
  });
  return covered;
}

function isRequiredImportFieldSatisfied(field, covered, config) {
  if (covered.has(field)) return true;
  const alternates = config.requiredImportFieldAlternates?.[field] ?? [];
  return alternates.some((alt) => covered.has(alt));
}

function getMissingRequiredImportFields(mapping, config) {
  const required = config.requiredImportFields ?? [];
  if (!required.length) return [];
  const covered = getMappedImportFields(mapping, config);
  return required.filter((field) => !isRequiredImportFieldSatisfied(field, covered, config));
}

function getUsedMappingTargets(mapping, excludeCol = null) {
  const used = new Set();
  Object.entries(mapping ?? {}).forEach(([col, target]) => {
    if (col === excludeCol) return;
    if (target && target !== "__skip__") used.add(target);
  });
  return used;
}

function isUploadReadyToContinue(upload, mapping, dataTypeKey) {
  const config = DATA_TYPES[dataTypeKey];
  if (!upload || upload.error) return true;
  if (config.importFields.length === 0) return true;
  if (upload.mappedRows == null) return false;
  return getMissingRequiredImportFields(mapping, config).length === 0;
}

// Convert a value that may be a JS Date, Excel serial number, or already-formatted string
// into a MM/DD/YYYY date string for date fields, or H:MM AM/PM for time fields.
function formatCellValue(field, value) {
  if (value === null || value === undefined || value === "") return "";

  // JS Date object (comes from XLSX.read with cellDates: true)
  if (value instanceof Date) {
    if (field.includes("DATE") || field.includes("Date")) {
      const m = String(value.getMonth() + 1).padStart(2, "0");
      const d = String(value.getDate()).padStart(2, "0");
      const y = value.getFullYear();
      return `${m}/${d}/${y}`;
    }
    if (field.includes("TIME") || field.includes("Time")) {
      const hours = value.getHours();
      const minutes = String(value.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      const h = hours % 12 || 12;
      return `${h}:${minutes} ${ampm}`;
    }
    return value.toLocaleDateString();
  }

  // Numeric or string-encoded Excel serial (e.g. 45306 or "45306.00011574074")
  if (field.includes("DATE") || field.includes("Date")) {
    const num = typeof value === "number" ? value : parseFloat(value);
    // Valid Excel date serial range: ~1970 (25569) to ~2100 (73050)
    if (!isNaN(num) && num > 25569 && num < 73050) {
      const utcDays = Math.floor(num) - 25569;
      const date = new Date(utcDays * 86400 * 1000);
      const m = String(date.getUTCMonth() + 1).padStart(2, "0");
      const d = String(date.getUTCDate()).padStart(2, "0");
      const y = date.getUTCFullYear();
      return `${m}/${d}/${y}`;
    }
  }

  return value;
}

function formatSampleDisplayValue(sourceCol, target, rawValue, autoSuggest = {}) {
  const fieldForFormat =
    target && target !== "__skip__" && !target.startsWith("__")
      ? target
      : autoSuggest[sourceCol] &&
          autoSuggest[sourceCol] !== "__skip__" &&
          !autoSuggest[sourceCol].startsWith("__")
        ? autoSuggest[sourceCol]
        : sourceCol;
  return String(formatCellValue(fieldForFormat, rawValue)).slice(0, 50);
}

function getPopulatedColumns(rows, candidateFields = null) {
  const rowsList = rows ?? [];
  if (!rowsList.length) return [];

  const fields = candidateFields?.length
    ? candidateFields
    : [...new Set(rowsList.flatMap((row) => Object.keys(row ?? {})))];

  return fields.filter((field) =>
    rowsList.some((row) => String(row?.[field] ?? "").trim() !== "")
  );
}

const EXPORT_FIELD_ALIASES = {
  clients: {
    CLIENT_FIRST_NAME: "First Name",
    CLIENT_LAST_NAME: "Last Name",
  },
  agents: {
    BA_FIRST_NAME: "First Name",
    BA_LAST_NAME: "Last Name",
  },
};

function applyExportFieldAliases(row, config = {}) {
  const aliases = EXPORT_FIELD_ALIASES[config.id];
  if (!aliases) return row ?? {};

  const out = { ...(row ?? {}) };
  Object.entries(aliases).forEach(([from, to]) => {
    const val = String(out[from] ?? "").trim();
    if (val && !String(out[to] ?? "").trim()) {
      out[to] = out[from];
    }
    delete out[from];
  });
  return out;
}

function normalizeMappedRowsForExport(mappedRows, importFields = [], config = {}) {
  const rowsList = (mappedRows ?? []).map((row) => applyExportFieldAliases(row, config));

  const fieldOrder = getExportFieldOrder(config, importFields);

  if (fieldOrder.length === 0) {
    const headers = getPopulatedColumns(rowsList);
    const rows = rowsList.map((row) => {
      const out = {};
      headers.forEach((field) => {
        out[field] = row?.[field] ?? "";
      });
      return out;
    });
    return { rows, headers };
  }

  const headers = getPopulatedColumns(rowsList, fieldOrder);
  const resolvedHeaders = headers.length > 0 ? headers : getPopulatedColumns(rowsList);
  const rows = rowsList.map((row) => {
    const out = {};
    resolvedHeaders.forEach((field) => {
      out[field] = formatCellValue(field, row?.[field] ?? "");
    });
    return out;
  });
  return { rows, headers: resolvedHeaders };
}

// Generate and download an ISN-formatted .xlsx file from mapped rows
function generateISNFile(mappedRows, importFields, label, config = {}) {
  const { rows: normalizedRows, headers } = normalizeMappedRowsForExport(mappedRows, importFields, config);
  const worksheet = XLSX.utils.json_to_sheet(normalizedRows, { header: headers });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, label);
  XLSX.writeFile(workbook, `ISN-Import-${label}.xlsx`);
}

// Get the effective rows for a data type upload (mapped if confirmed, raw otherwise)
function effectiveRows(upload) {
  if (!upload) return [];
  return upload.mappedRows ?? upload.rows ?? [];
}

// ── Sample data & download ────────────────────────────────────────────────────

const SAMPLE_ROWS = {
  templates: [
    { "Template Name": "General Home Inspection", "Category": "Roof", "Item": "Shingles", "Description": "Inspect shingle condition for wear, missing, or curling", "Rating": "Satisfactory" },
    { "Template Name": "General Home Inspection", "Category": "Electrical", "Item": "Panel", "Description": "Inspect main electrical panel and breakers", "Rating": "Needs Attention" },
    { "Template Name": "General Home Inspection", "Category": "Plumbing", "Item": "Water Heater", "Description": "Check age, condition, and proper installation", "Rating": "Satisfactory" },
  ],
  clients: [
    { "First Name": "Sarah", "Last Name": "Johnson", "Address 1": "742 Evergreen Terrace", "City": "Springfield", "State Abbreviation": "IL", "Zip": "62701", "Email Address": "sarah.johnson@email.com", "Home Phone": "217-555-0198", "Mobile": "217-555-0198" },
    { "First Name": "Michael", "Last Name": "Torres", "Address 1": "1600 Pennsylvania Ave", "City": "Chicago", "State Abbreviation": "IL", "Zip": "60601", "Email Address": "m.torres@email.com", "Home Phone": "312-555-0234", "Mobile": "312-555-0234" },
  ],
  agents: [
    { "First Name": "Linda", "Last Name": "Park", "Agency": "Park Realty Group", "Email Address": "lpark@parkrealty.com", "Work Phone": "630-555-0155", "Website Address": "www.parkrealtygroup.com" },
    { "First Name": "James", "Last Name": "Walsh", "Agency": "Walsh Real Estate", "Email Address": "jwalsh@walshre.com", "Work Phone": "312-555-0288" },
  ],
  history: [
    { "ORDER_DATE": "01/15/2024", "ORDER_TIME": "9:00 AM", "ORDER_TYPE": "Residential", "FOUNDATION_TYPE": "Slab", "ORDER_SQFT": "2100", "ORDER_YRBUILT": "1998", "ORDER_FEE": "450", "ORDER_ADDRESS1": "123 Maple Drive", "ORDER_CITY": "Aurora", "ORDER_STATE_ABBREVIATION": "IL", "ORDER_ZIP": "60502", "CLIENT_FIRST_NAME": "Tom", "CLIENT_LAST_NAME": "Baker", "CLIENT_EMAIL": "tom.baker@email.com", "CLIENT_MOBILE_PHONE": "630-555-0101", "INSPECTOR_NAME": "John Smith" },
    { "ORDER_DATE": "02/03/2024", "ORDER_TIME": "10:30 AM", "ORDER_TYPE": "Residential", "FOUNDATION_TYPE": "Basement", "ORDER_SQFT": "1850", "ORDER_YRBUILT": "2005", "ORDER_FEE": "400", "ORDER_ADDRESS1": "456 Birch Lane", "ORDER_CITY": "Elgin", "ORDER_STATE_ABBREVIATION": "IL", "ORDER_ZIP": "60120", "CLIENT_FIRST_NAME": "Amy", "CLIENT_LAST_NAME": "Chen", "CLIENT_EMAIL": "amy.chen@email.com", "CLIENT_MOBILE_PHONE": "847-555-0188", "INSPECTOR_NAME": "John Smith" },
  ],
};

// Sample file column order first, then any additional ISN import fields — keeps export aligned with samples.
function getExportFieldOrder(config = {}, importFields = []) {
  const fullFields = importFields.length ? importFields : (config.importFields ?? []);
  const appendFields = Object.keys(config.appendColumns ?? {}).filter((field) => !fullFields.includes(field));
  const allFields = [...fullFields, ...appendFields];
  const sampleRow = SAMPLE_ROWS[config.id]?.[0];
  if (!sampleRow) return allFields;
  const sampleOrder = Object.keys(sampleRow);
  const sampleSet = new Set(sampleOrder);
  return [...sampleOrder, ...allFields.filter((field) => !sampleSet.has(field))];
}

function downloadSampleCSV(dataType) {
  const rows = SAMPLE_ROWS[dataType];
  if (!rows?.length) return;

  if (dataType === "templates") {
    const headers = Object.keys(rows[0] ?? {});
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Templates");
    XLSX.writeFile(workbook, "SpectoraImportTemplate.xlsx");
    return;
  }

  const headers = Object.keys(rows[0] ?? {});
  const csvLines = [
    headers.join(","),
    ...rows.map((row) =>
      headers.map((h) => {
        const val = String(row[h] ?? "");
        return val.includes(",") ? `"${val}"` : val;
      }).join(",")
    ),
  ];
  const blob = new Blob([csvLines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sample-${dataType}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Constants ─────────────────────────────────────────────────────────────────

const WIZARD_STEPS = ["Templates", "Clients", "Agents", "Legacy Inspection History", "Review & Submit"];

const DATA_TYPES = {
  templates: {
    id: "templates",
    label: "Templates",
    icon: <DescriptionOutlinedIcon />,
    description: "Import your existing inspection templates so your team can hit the ground running.",
    hint: "Download the sample file above to see the exact format. Compatible with Spectora and most major inspection platforms.",
    color: "#42a5f5",
    colorBg: "#e3f2fd",
    stepIndex: 0,
    // Templates already match ISN format — no column mapping needed
    importFields: [],
    autoSuggest: {},
  },
  clients: {
    id: "clients",
    label: "Clients",
    icon: <PeopleAltOutlinedIcon />,
    description: "Bring your client contact list along — no need to re-enter anything by hand.",
    hint: "Supports .xlsx and .xls files. All columns are optional except First Name.",
    color: "#42a5f5",
    colorBg: "#e3f2fd",
    stepIndex: 1,
    importFields: [
      "First Name", "Last Name",
      "Address 1", "Address 2", "City", "State Abbreviation", "Zip",
      "Email Address",
      "Home Phone", "Mobile",
      "Work Phone", "Work Fax", "Home Fax", "Notes",
    ],
    requiredImportFields: ["First Name"],
    requiredImportFieldAlternates: {
      "First Name": ["CLIENT_FIRST_NAME"],
    },
    appendColumns: { "Mass opt": 1 },
    autoSuggest: {
      // Spectora-style source columns
      "Name": "__split_name__",
      "Full Name": "__split_client_name__",
      "First Name and Last Name": "__split_client_name__",
      "Client Name": "__split_client_name__",
      "Email": "Email Address",
      "Email address": "Email Address",
      "Email Address": "Email Address",
      "Phone": "Home Phone",
      "Phone number": "Home Phone",
      "Phone Number": "Home Phone",
      "Phone #": "Home Phone",
      "Client Phone": "Home Phone",
      "Contact Phone": "Home Phone",
      "Mobile Phone": "Mobile",
      "Mobile": "Mobile",
      "Cell": "Mobile",
      "Cell Phone": "Mobile",
      "Work Phone": "Work Phone",
      "Business Phone": "Work Phone",
      "Office Phone": "Work Phone",
      "Number of inspections": "__skip__",
      "Number of Inspections": "__skip__",
      "Last inspection date": "__skip__",
      "Last Inspection Date": "__skip__",
      "Tags": "__notes__",
      // Direct ISN field pass-throughs
      "First Name": "First Name",
      "Last Name": "Last Name",
      "Address 1": "Address 1",
      "Address 2": "Address 2",
      "City": "City",
      "State Abbreviation": "State Abbreviation",
      "Zip": "Zip",
      "Home Phone": "Home Phone",
      "Home Fax": "Home Fax",
      "Work Fax": "Work Fax",
      "Notes": "Notes",
    },
  },
  agents: {
    id: "agents",
    label: "Agents",
    icon: <ContactsOutlinedIcon />,
    description: "Keep your real estate agent relationships intact from day one.",
    hint: "Supports .xlsx and .xls files. All columns are optional except First Name.",
    color: "#42a5f5",
    colorBg: "#e3f2fd",
    stepIndex: 2,
    importFields: [
      "First Name", "Last Name", "Agency",
      "Agency Address 1", "Agency Address 2", "Agency City", "Agency State Abbreviation", "Agency Zip",
      "Email Address", "Work Phone", "Website Address",
      "Address 1", "Address 2", "City", "State Abbreviation", "Zip",
      "Work Fax", "Mobile", "Home Phone", "Home Fax", "Notes",
    ],
    requiredImportFields: ["First Name"],
    requiredImportFieldAlternates: {
      "First Name": ["BA_FIRST_NAME"],
    },
    appendColumns: { "Mass opt": 1 },
    autoSuggest: {
      // ISN export columns
      "UUID": "__skip__",
      "ISN ID": "__skip__",
      "First Name": "First Name",
      "Last Name": "Last Name",
      "Birthday": "__skip__",
      "Agency": "Agency",
      "Agency Phone": "__skip__",
      "Client's Agent # Inspections": "__skip__",
      "Seller's Agent # Inspections": "__skip__",
      "Last Inspection": "__skip__",
      "Days Since Last Inspection": "__skip__",
      "Phone": "Work Phone",
      "Second Phone": "Mobile",
      "Email": "Email Address",
      "Address": "Address 1",
      "City": "City",
      "State": "State Abbreviation",
      "Country": "__skip__",
      "Zip": "Zip",
      "Image": "__skip__",
      "Website": "Website Address",
      "Facebook Link": "__skip__",
      "Twitter Link": "__skip__",
      "LinkedIn Link": "__skip__",
      "Instagram Link": "__skip__",
      "Tiktok Link": "__skip__",
      "Added": "__skip__",
      "Updated": "__skip__",
      "Tags": "__skip__",
      "Agent Team": "__skip__",
      // Common alternate column names
      "Name": "__split_ba_name__",
      "Full Name": "__split_ba_name__",
      "First Name and Last Name": "__split_ba_name__",
      "Agent Name": "__split_ba_name__",
      "Email Address": "Email Address",
      "Email address": "Email Address",
      "Phone Number": "Work Phone",
      "Phone number": "Work Phone",
      "Number of Inspections": "__skip__",
      "Number of inspections": "__skip__",
      "Social Media Links": "__skip__",
      "Social media links": "__skip__",
      // Direct ISN import field pass-throughs
      "Agency Address 1": "Agency Address 1",
      "Agency Address 2": "Agency Address 2",
      "Agency City": "Agency City",
      "Agency State Abbreviation": "Agency State Abbreviation",
      "Agency Zip": "Agency Zip",
      "Website Address": "Website Address",
      "Address 1": "Address 1",
      "Address 2": "Address 2",
      "State Abbreviation": "State Abbreviation",
      "Work Phone": "Work Phone",
      "Work Fax": "Work Fax",
      "Mobile": "Mobile",
      "Home Phone": "Home Phone",
      "Home Fax": "Home Fax",
      "Notes": "Notes",
    },
  },
  history: {
    id: "history",
    label: "Legacy Inspection History",
    icon: <HistoryOutlinedIcon />,
    description: "Preserve your complete legacy inspection history for reference, reporting, and continuity.",
    hint: "Export your past inspections from your current software. Date format: MM/DD/YYYY.",
    color: "#42a5f5",
    colorBg: "#e3f2fd",
    stepIndex: 3,
    importFields: [
      "ORDER_DATE", "ORDER_TIME", "ORDER_TYPE", "FOUNDATION_TYPE", "ORDER_REPORT_NUMBER",
      "ORDER_SQFT", "ORDER_YRBUILT", "ORDER_FEE",
      "ORDER_ADDRESS1", "ORDER_ADDRESS2", "ORDER_CITY", "ORDER_STATE_ABBREVIATION", "ORDER_ZIP",
      "CLIENT_FIRST_NAME", "CLIENT_LAST_NAME",
      "CLIENT_ADDRESS1", "CLIENT_ADDRESS2", "CLIENT_CITY", "CLIENT_STATE_ABBREVIATION", "CLIENT_ZIP",
      "CLIENT_EMAIL", "CLIENT_MOBILE_PHONE",
      "BA_FIRST_NAME", "BA_LAST_NAME", "BA_EMAIL",
      "BA_ADDRESS1", "BA_CITY", "BA_STATE", "BA_ZIP", "BA_AGENCY",
      "BA_MOBILE_PHONE", "BA_OFFICE_PHONE",
      "SA_FIRST_NAME", "SA_LAST_NAME", "SA_EMAIL",
      "SA_ADDRESS1", "SA_CITY", "SA_STATE", "SA_ZIP", "SA_AGENCY",
      "SA_MOBILE_PHONE", "SA_OFFICE_PHONE",
      "INSPECTOR_NAME", "NOTES",
    ],
    appendColumns: { "Mass opt": 1 },
    autoSuggest: {
      // Spectora export columns → ISN import columns
      "Order ID": "__skip__",
      "Date": "ORDER_DATE",
      "Time": "ORDER_TIME",
      "Total": "ORDER_FEE",
      "Outstanding": "__skip__",
      "Address": "ORDER_ADDRESS1",
      "City": "ORDER_CITY",
      "State": "ORDER_STATE_ABBREVIATION",
      "Zip": "ORDER_ZIP",
      "County": "__skip__",
      "Square Feet": "ORDER_SQFT",
      "Year Built": "ORDER_YRBUILT",
      "Foundation": "FOUNDATION_TYPE",
      "Description": "ORDER_TYPE",
      "Inspector 1": "INSPECTOR_NAME",
      "Inspector 2": "__skip__",
      "Inspector 3": "__skip__",
      "Inspector 4": "__skip__",
      "Inspector 5": "__skip__",
      "Client": "__split_client_name__",
      "Client Email": "CLIENT_EMAIL",
      "Client Phone": "CLIENT_MOBILE_PHONE",
      "Client Tags": "__skip__",
      "Agent": "__split_ba_name__",
      "Agent Email": "BA_EMAIL",
      "Agent Phone": "BA_MOBILE_PHONE",
      "Agent Agency Name": "BA_AGENCY",
      "Agent Tags": "__skip__",
      "Referral Source": "__skip__",
      "Scheduled By": "__skip__",
      "Agreements Signed?": "__skip__",
      "Canceled?": "__skip__",
      "Paid?": "__skip__",
      "Published?": "__skip__",
      "Confirmed?": "__skip__",
      "Discount Code": "__skip__",
      "Payment Date": "__skip__",
      "Payment Type": "__skip__",
      "Payment Txn": "__skip__",
      "Payment Total": "__skip__",
      "Payment Fee": "__skip__",
      "Payment Net": "__skip__",
      "Fee 1 Name": "__skip__",
      "Fee 1": "ORDER_FEE",
      // Direct ISN field pass-throughs
      "ORDER_DATE": "ORDER_DATE",
      "ORDER_TIME": "ORDER_TIME",
      "ORDER_TYPE": "ORDER_TYPE",
      "FOUNDATION_TYPE": "FOUNDATION_TYPE",
      "ORDER_REPORT_NUMBER": "ORDER_REPORT_NUMBER",
      "ORDER_SQFT": "ORDER_SQFT",
      "ORDER_YRBUILT": "ORDER_YRBUILT",
      "ORDER_FEE": "ORDER_FEE",
      "ORDER_ADDRESS1": "ORDER_ADDRESS1",
      "ORDER_ADDRESS2": "ORDER_ADDRESS2",
      "ORDER_CITY": "ORDER_CITY",
      "ORDER_STATE_ABBREVIATION": "ORDER_STATE_ABBREVIATION",
      "ORDER_ZIP": "ORDER_ZIP",
      "CLIENT_FIRST_NAME": "CLIENT_FIRST_NAME",
      "CLIENT_LAST_NAME": "CLIENT_LAST_NAME",
      "CLIENT_ADDRESS1": "CLIENT_ADDRESS1",
      "CLIENT_ADDRESS2": "CLIENT_ADDRESS2",
      "CLIENT_CITY": "CLIENT_CITY",
      "CLIENT_STATE_ABBREVIATION": "CLIENT_STATE_ABBREVIATION",
      "CLIENT_ZIP": "CLIENT_ZIP",
      "CLIENT_EMAIL": "CLIENT_EMAIL",
      "CLIENT_MOBILE_PHONE": "CLIENT_MOBILE_PHONE",
      "BA_FIRST_NAME": "BA_FIRST_NAME",
      "BA_LAST_NAME": "BA_LAST_NAME",
      "BA_EMAIL": "BA_EMAIL",
      "BA_ADDRESS1": "BA_ADDRESS1",
      "BA_CITY": "BA_CITY",
      "BA_STATE": "BA_STATE",
      "BA_ZIP": "BA_ZIP",
      "BA_AGENCY": "BA_AGENCY",
      "BA_MOBILE_PHONE": "BA_MOBILE_PHONE",
      "BA_OFFICE_PHONE": "BA_OFFICE_PHONE",
      "SA_FIRST_NAME": "SA_FIRST_NAME",
      "SA_LAST_NAME": "SA_LAST_NAME",
      "SA_EMAIL": "SA_EMAIL",
      "SA_ADDRESS1": "SA_ADDRESS1",
      "SA_CITY": "SA_CITY",
      "SA_STATE": "SA_STATE",
      "SA_ZIP": "SA_ZIP",
      "SA_AGENCY": "SA_AGENCY",
      "SA_MOBILE_PHONE": "SA_MOBILE_PHONE",
      "SA_OFFICE_PHONE": "SA_OFFICE_PHONE",
      "INSPECTOR_NAME": "INSPECTOR_NAME",
      "NOTES": "NOTES",
    },
  },
};

const DATA_TYPE_ORDER = ["templates", "clients", "agents", "history"];

const ACCEPTED_FILE_EXTENSIONS = [".xlsx", ".xls", ".csv"];
const ACCEPTED_FILE_ACCEPT = ACCEPTED_FILE_EXTENSIONS.join(",");
const INVALID_FILE_TYPE_MESSAGE = "Please upload a spreadsheet file (.xlsx, .xls, or .csv).";

function getFileExtension(fileName) {
  const match = fileName?.match(/(\.[^./\\]+)$/i);
  return match ? match[1].toLowerCase() : "";
}

function isAcceptedSpreadsheetFile(file) {
  if (!file?.name) return false;
  return ACCEPTED_FILE_EXTENSIONS.includes(getFileExtension(file.name));
}

const UPLOAD_FORM_FIELDS = {
  templates: "template_file",
  clients: "client_file",
  agents: "agent_file",
  history: "inspection_history_file",
};

function buildSubmissionFile(upload, dataTypeKey) {
  if (!upload) return null;
  const config = DATA_TYPES[dataTypeKey];
  const rows = effectiveRows(upload);

  if (upload.mappedRows != null && rows.length > 0) {
    const { rows: normalizedRows, headers } = normalizeMappedRowsForExport(rows, config.importFields, config);
    const worksheet = XLSX.utils.json_to_sheet(normalizedRows, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, config.label);
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const stem = upload.file?.name?.replace(/\.[^.]+$/, "") || config.label;
    return new File([buffer], `${stem}.xlsx`, {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  }

  return upload.file ?? null;
}

// ── DropZone ──────────────────────────────────────────────────────────────────

function DropZone({ dataType, uploadedFile, onFileUpload, onFileClear }) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragRejected, setDragRejected] = useState(false);
  const inputRef = useRef(null);
  const config = DATA_TYPES[dataType];

  const rejectFile = useCallback((message = INVALID_FILE_TYPE_MESSAGE) => {
    toast.error(message);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const parseFile = useCallback((file) => {
    if (!isAcceptedSpreadsheetFile(file)) {
      rejectFile();
      return;
    }

    const ext = getFileExtension(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let workbook;
        if (ext === ".csv") {
          const text = e.target.result;
          workbook = XLSX.read(text, { type: "string" });
        } else {
          const data = new Uint8Array(e.target.result);
          workbook = XLSX.read(data, { type: "array", cellDates: true });
        }
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        const rows = filterNonEmptyRows(json);
        const headers = rows.length > 0 ? Object.keys(rows[0]) : (json.length > 0 ? Object.keys(json[0]) : []);
        onFileUpload({ file, rows, headers, preview: rows.slice(0, 5), mappedRows: null });
      } catch {
        onFileUpload({ file, rows: [], headers: [], preview: [], mappedRows: null, error: "Could not parse file. Please check the format." });
      }
    };

    if (ext === ".csv") {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  }, [onFileUpload, rejectFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    setDragRejected(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!isAcceptedSpreadsheetFile(file)) {
      rejectFile();
      return;
    }
    parseFile(file);
  }, [parseFile, rejectFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    const items = e.dataTransfer?.items;
    let rejected = false;

    if (items?.length) {
      const fileItems = [...items].filter((item) => item.kind === "file");
      rejected = fileItems.length > 0 && fileItems.every((item) => {
        const type = item.type?.toLowerCase() ?? "";
        if (!type) return false;
        return (
          type.startsWith("video/") ||
          type.startsWith("audio/") ||
          type.startsWith("image/") ||
          type === "application/pdf" ||
          type === "text/plain"
        );
      });
    }

    setDragRejected(rejected);
    setIsDragging(!rejected);
    e.dataTransfer.dropEffect = rejected ? "none" : "copy";
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
    setDragRejected(false);
  }, []);

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!isAcceptedSpreadsheetFile(file)) {
      rejectFile();
      return;
    }
    parseFile(file);
  };

  if (uploadedFile) {
    return (
      <div className="upload-success-card" style={{ borderColor: config.color }}>
        <div className="upload-success-icon" style={{ background: config.colorBg, color: config.color }}>
          <InsertDriveFileIcon />
        </div>
        <div className="upload-success-info">
          <span className="upload-filename">{uploadedFile.file.name}</span>
          <span className="upload-rowcount" style={{ color: config.color }}>
            {effectiveRows(uploadedFile).length} records found
          </span>
          {uploadedFile.error && (
            <span className="upload-error">{uploadedFile.error}</span>
          )}
        </div>
        <button className="upload-clear-btn" onClick={onFileClear} title="Remove file">
          <DeleteOutlineIcon fontSize="small" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`dropzone${isDragging ? " dropzone--dragging" : ""}${dragRejected ? " dropzone--rejected" : ""}`}
      style={{ borderColor: dragRejected ? "#d32f2f" : isDragging ? config.color : undefined }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_FILE_ACCEPT}
        hidden
        onChange={handleChange}
      />
      <div className="dropzone-icon" style={{ color: config.color }}>
        <CloudUploadIcon fontSize="large" />
      </div>
      <p className="dropzone-title">Drop your spreadsheet here</p>
      <p className="dropzone-subtitle">or <span style={{ color: config.color }}>browse files</span> — .xlsx, .xls, or .csv</p>
    </div>
  );
}

// ── PreviewTable (used only for Templates — no mapping needed) ────────────────

function PreviewTable({ uploadedFile, dataType }) {
  const config = DATA_TYPES[dataType];
  if (!uploadedFile || !uploadedFile.preview?.length) return null;

  const columns = uploadedFile.headers.slice(0, 6);

  return (
    <div className="preview-section">
      <div className="preview-header">
        <Typography variant="subtitle2" className="preview-label">
          Preview — first {uploadedFile.preview.length} rows
        </Typography>
        <Chip
          label={`${effectiveRows(uploadedFile).length} total records`}
          size="small"
          sx={{ background: config.colorBg, color: config.color, fontWeight: 600, fontSize: "12px" }}
        />
      </div>
      <TableContainer component={Paper} elevation={0} className="preview-table-container">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col} className="preview-col-header" style={{ color: config.color }}>
                  {col}
                </TableCell>
              ))}
              {uploadedFile.headers.length > 6 && (
                <TableCell className="preview-col-header" style={{ color: "#999" }}>
                  +{uploadedFile.headers.length - 6} more…
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {uploadedFile.preview.map((row, i) => (
              <TableRow key={i} hover>
                {columns.map((col) => (
                  <TableCell key={col} className="preview-cell">
                    {String(row[col] ?? "—")}
                  </TableCell>
                ))}
                {uploadedFile.headers.length > 6 && <TableCell />}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

// ── ColumnMapper ──────────────────────────────────────────────────────────────

function ColumnMapper({ dataType, uploadedFile, mapping, onMappingChange, onConfirm }) {
  const config = DATA_TYPES[dataType];
  if (!uploadedFile || !uploadedFile.headers.length) return null;

  const { headers, rows } = uploadedFile;
  const sampleRow = rows[0] ?? {};
  const importFields = config.importFields;

  // Split headers into two zones — hide columns with no data from Zone 2
  const colHasData = (col) => rows.some((row) => String(row[col] ?? "").trim() !== "");
  const activeCols = headers.filter((col) => (mapping[col] ?? "__skip__") !== "__skip__");
  const skippedCols = headers.filter((col) => (mapping[col] ?? "__skip__") === "__skip__" && colHasData(col));

  function removeCol(col) {
    onMappingChange({ ...mapping, [col]: "__skip__" });
  }

  function restoreCol(col) {
    onMappingChange({ ...mapping, [col]: getRestorableMappingTarget(col, config) });
  }

  // Build live mapped preview from current mapping (skip rows with no mapped values)
  const previewRows = applyMapping(rows, mapping, config).slice(0, 3);
  const previewKeys = [...new Set(previewRows.flatMap((r) => Object.keys(r)))];

  const mappingTokens = getMappingTokensForConfig(config);
  const tokenLabel = (token) => MAPPING_TOKENS[token] ?? token;
  const missingRequired = getMissingRequiredImportFields(mapping, config);

  return (
    <div className="column-mapper">
      <div className="column-mapper-header">
        <Typography variant="subtitle2" className="column-mapper-title">
          Map your columns
        </Typography>
        <Typography variant="body2" className="column-mapper-desc">
          We've pre-filled the mapping based on common column names from Spectora and other platforms.
          Review each row and adjust if needed — then confirm to continue.
        </Typography>
      </div>

      {/* Zone 1: Active (will be imported) */}
      <div className="mapping-table-wrap">
        <table className="mapping-table">
          <thead>
            <tr>
              <th className="mapping-th">Export Value</th>
              <th className="mapping-th">Sample value</th>
              <th className="mapping-th">Import Value</th>
              <th className="mapping-th mapping-col-remove-header"></th>
            </tr>
          </thead>
          <tbody>
            {activeCols.map((col) => {
              const target = mapping[col];
              const sampleVal = formatSampleDisplayValue(col, target, sampleRow[col], config.autoSuggest);
              const usedTargets = getUsedMappingTargets(mapping, col);

              return (
                <tr key={col} className="mapping-row">
                  <td className="mapping-cell mapping-source-col">
                    <span className="mapping-col-name">{col}</span>
                  </td>
                  <td className="mapping-cell mapping-sample-val">
                    {sampleVal ? (
                      <span className="mapping-sample-text">{sampleVal}</span>
                    ) : (
                      <em className="mapping-sample-empty">empty</em>
                    )}
                  </td>
                  <td className="mapping-cell mapping-target-cell">
                    <div className="mapping-target-content">
                      <select
                        className="mapping-select"
                        value={target}
                        onChange={(e) => onMappingChange({ ...mapping, [col]: e.target.value })}
                      >
                        {Object.keys(mappingTokens).length > 0 && (
                          <optgroup label="Special Actions">
                            {Object.entries(mappingTokens).map(([token, label]) => (
                              <option key={token} value={token} disabled={usedTargets.has(token)}>
                                {label}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        <optgroup label="ISN Import Fields">
                          {importFields.map((field) => (
                            <option key={field} value={field} disabled={usedTargets.has(field)}>
                              {field}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                      <span className="mapping-target-badge" style={{ color: config.color, borderColor: config.colorBg, background: config.colorBg }}>
                        {tokenLabel(target)}
                      </span>
                    </div>
                  </td>
                  <td className="mapping-cell mapping-remove-cell">
                    <button
                      className="col-remove-btn"
                      onClick={() => removeCol(col)}
                      title="Don't import this column"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
            {activeCols.length === 0 && (
              <tr>
                <td colSpan={4} className="mapping-empty-msg">
                  No columns selected for import. Use the section below to add columns.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Zone 2: Skipped (not importing) */}
      {skippedCols.length > 0 && (
        <div className="skipped-cols-section">
          <Typography variant="caption" className="skipped-cols-label">
            Not importing these columns ({skippedCols.length})
          </Typography>
          <div className="skipped-cols-chips">
            {skippedCols.map((col) => {
              const canRestore = canRestoreSkippedColumn(col, config);
              return (
                <div key={col} className={`skipped-col-chip${canRestore ? "" : " skipped-col-chip--no-import"}`} title={formatSampleDisplayValue(col, mapping[col], sampleRow[col], config.autoSuggest)}>
                  <span className="skipped-col-name">{col}</span>
                  {canRestore && (
                    <button
                      className="skipped-col-restore-btn"
                      onClick={() => restoreCol(col)}
                      title="Add to import"
                    >
                      + Add
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Live mapped preview */}
      {previewRows.length > 0 && previewKeys.length > 0 && (
        <div className="mapped-preview-section">
          <div className="mapped-preview-header">
            <Typography variant="caption" className="mapped-preview-label">
              Preview after mapping — {previewRows.length} sample rows
            </Typography>
          </div>
          <TableContainer component={Paper} elevation={0} className="preview-table-container">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {previewKeys.map((k) => (
                    <TableCell key={k} className="preview-col-header" style={{ color: config.color }}>
                      {k}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((row, i) => (
                  <TableRow key={i} hover>
                    {previewKeys.map((k) => (
                      <TableCell key={k} className="preview-cell">
                        {row[k] || "—"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}

      <div className="mapper-confirm-row">
        {missingRequired.length > 0 && (
          <Typography variant="body2" className="mapper-required-error" color="error">
            Required field{missingRequired.length > 1 ? "s" : ""} must be mapped before continuing:{" "}
            <strong>{missingRequired.join(", ")}</strong>
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={() => onConfirm(applyMapping(rows, mapping, config))}
          className="mapper-confirm-btn"
          endIcon={<CheckIcon />}
          disabled={missingRequired.length > 0}
          sx={{ background: config.color, "&:hover": { background: "#1461A8" } }}
        >
          Mapping looks good — confirm
        </Button>
        <Typography variant="caption" className="mapper-confirm-note">
          You can adjust and re-confirm at any time.
        </Typography>
      </div>
    </div>
  );
}

// ── MappingConfirmedBanner ────────────────────────────────────────────────────

function MappingConfirmedBanner({ uploadedFile, dataType, onRemap }) {
  const config = DATA_TYPES[dataType];
  const count = uploadedFile.mappedRows?.length ?? 0;

  return (
    <div className="mapping-confirmed-banner" style={{ borderColor: config.color, background: config.colorBg }}>
      <CheckCircleOutlineIcon className="mapping-confirmed-icon" style={{ color: config.color }} />
      <div className="mapping-confirmed-text">
        <Typography variant="subtitle2" style={{ color: config.color, fontWeight: 700 }}>
          Mapping confirmed
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {count.toLocaleString()} records ready to import with your column mapping applied.
        </Typography>
      </div>
      <div className="mapping-confirmed-actions">
        <button
          className="download-mapped-btn"
          onClick={() => generateISNFile(uploadedFile.mappedRows, config.importFields, config.label, config)}
          title="Download ISN-format file"
        >
          <DownloadOutlinedIcon fontSize="small" />
          Download file
        </button>
        <button className="remap-btn" onClick={onRemap} title="Adjust column mapping">
          <EditOutlinedIcon fontSize="small" />
          Re-map
        </button>
      </div>
    </div>
  );
}

// ── UploadStep ────────────────────────────────────────────────────────────────

function UploadStep({ dataType, uploadedFile, onFileUpload, onFileClear, mapping, onMappingChange, onMappingConfirm }) {
  const config = DATA_TYPES[dataType];
  const needsMapping = config.importFields.length > 0;
  const isMappingConfirmed = needsMapping && uploadedFile?.mappedRows != null;
  const showMapper = needsMapping && uploadedFile && !uploadedFile.error && !isMappingConfirmed;
  const showRawPreview = !needsMapping && uploadedFile && !uploadedFile.error;

  return (
    <div className="wizard-step-content">
      <div className="step-type-header">
        <div className="step-type-icon" style={{ background: config.colorBg, color: config.color, border: `1px solid ${config.color}` }}>
          {config.icon}
        </div>
        <div>
          <Typography variant="h6" className="step-type-title">{config.label}</Typography>
          <Typography variant="body2" className="step-type-desc">{config.description}</Typography>
        </div>
      </div>

      <div className="sample-download-row">
        <button
          className="sample-download-btn"
          onClick={() => downloadSampleCSV(dataType)}
          style={{ color: config.color }}
        >
          <DownloadOutlinedIcon fontSize="inherit" />
          Download sample file
        </button>
      </div>

      <DropZone
        dataType={dataType}
        uploadedFile={uploadedFile}
        onFileUpload={onFileUpload}
        onFileClear={onFileClear}
      />

      <p className="upload-hint">{config.hint}</p>

      {/* Column mapper (non-template types) */}
      {isMappingConfirmed && (
        <MappingConfirmedBanner
          uploadedFile={uploadedFile}
          dataType={dataType}
          onRemap={() => onMappingConfirm(null)}
        />
      )}
      {showMapper && (
        <ColumnMapper
          dataType={dataType}
          uploadedFile={uploadedFile}
          mapping={mapping}
          onMappingChange={onMappingChange}
          onConfirm={onMappingConfirm}
        />
      )}

      {/* Raw preview for templates */}
      {showRawPreview && (
        <PreviewTable uploadedFile={uploadedFile} dataType={dataType} />
      )}
    </div>
  );
}

// ── UserDetailsStep ──────────────────────────────────────────────────────────

function UserDetailsStep({ userDetails, onChange, onBack, onContinue }) {
  const [errors, setErrors] = useState({});

  function validate() {
    const errs = {};
    if (!userDetails.name?.trim()) errs.name = "Full name is required";
    if (!userDetails.company?.trim()) errs.company = "Company name is required";
    if (!userDetails.email?.trim()) {
      errs.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userDetails.email)) {
      errs.email = "Enter a valid email address";
    }
    return errs;
  }

  function handleContinue() {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
    } else {
      onContinue();
    }
  }

  function handleChange(field, value) {
    onChange({ ...userDetails, [field]: value });
    if (errors[field]) setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
  }

  function handlePhoneChange(value) {
    handleChange("phone", String(value).replace(/\D/g, "").slice(0, 10));
  }

  function handlePhoneBlur() {
    const digits = String(userDetails.phone ?? "").replace(/\D/g, "");
    handleChange("phone", digits ? formatPhoneNumber(digits) : "");
  }

  return (
    <div className="user-details-step">
      <div className="user-details-logo">
        <img
          src="https://cdn.builder.io/api/v1/image/assets%2F9b667fe848dd420d92676f1bc60bc542%2F93fd7f1513404b1b8592edbcab89def1?format=webp&width=800&height=1200"
          alt="ISN — Inspection Support Network"
          className="isn-logo-img"
        />
      </div>

      <div className="welcome-badge">
        <EastIcon fontSize="small" />
        <span>Data Migration Wizard</span>
      </div>

      <Typography variant="h5" className="user-details-title">
        Let's get started
      </Typography>
      <Typography variant="body2" className="user-details-subtitle">
        Tell us a bit about yourself so our team can assist with your migration if needed.
      </Typography>

      <div className="user-details-form">
        <div className="user-details-field-row">
          <div className="user-details-field">
            <label className="user-details-label">Full Name <span className="required-star">*</span></label>
            <input
              className={`user-details-input${errors.name ? " user-details-input--error" : ""}`}
              placeholder="Jane Smith"
              value={userDetails.name ?? ""}
              onChange={(e) => handleChange("name", e.target.value)}
            />
            {errors.name && <span className="user-details-error">{errors.name}</span>}
          </div>
          <div className="user-details-field">
            <label className="user-details-label">Company Name <span className="required-star">*</span></label>
            <input
              className={`user-details-input${errors.company ? " user-details-input--error" : ""}`}
              placeholder="Acme Home Inspections"
              value={userDetails.company ?? ""}
              onChange={(e) => handleChange("company", e.target.value)}
            />
            {errors.company && <span className="user-details-error">{errors.company}</span>}
          </div>
        </div>

        <div className="user-details-field">
          <label className="user-details-label">Account Code (Company Key)</label>
          <input
            className="user-details-input"
            placeholder="e.g. ACME123"
            value={userDetails.accountCode ?? ""}
            onChange={(e) => handleChange("accountCode", e.target.value)}
          />
          <span className="user-details-field-hint">
            This is found in your ISN account settings. Leave blank if you don't know it yet.
          </span>
        </div>

        <div className="user-details-field-row">
          <div className="user-details-field">
            <label className="user-details-label">Email Address <span className="required-star">*</span></label>
            <input
              type="email"
              className={`user-details-input${errors.email ? " user-details-input--error" : ""}`}
              placeholder="jane@acmeinspections.com"
              value={userDetails.email ?? ""}
              onChange={(e) => handleChange("email", e.target.value)}
            />
            {errors.email && <span className="user-details-error">{errors.email}</span>}
          </div>
          <div className="user-details-field">
            <label className="user-details-label">Phone Number</label>
            <input
              type="tel"
              className="user-details-input"
              placeholder="(555) 555-5555"
              inputMode="numeric"
              value={userDetails.phone ?? ""}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onBlur={handlePhoneBlur}
            />
          </div>
        </div>
      </div>

      <div className="user-details-actions">
        <Button
          variant="contained"
          size="large"
          className="welcome-cta-btn"
          onClick={handleContinue}
          endIcon={<ArrowForwardIcon />}
        >
          Continue to Import
        </Button>
        <p className="welcome-note">Takes about 5 minutes. You can skip any section.</p>
      </div>

      <button className="user-details-back-link" onClick={onBack}>
        <ArrowBackIcon fontSize="inherit" /> Back to overview
      </button>
    </div>
  );
}

// ── WelcomeStep ───────────────────────────────────────────────────────────────

function WelcomeStep({ onStart }) {
  return (
    <div className="welcome-step">
      <div className="welcome-isn-logo">
        <img
          src="https://cdn.builder.io/api/v1/image/assets%2F9b667fe848dd420d92676f1bc60bc542%2F93fd7f1513404b1b8592edbcab89def1?format=webp&width=800&height=1200"
          alt="ISN — Inspection Support Network"
          className="isn-logo-img"
        />
      </div>
      <div className="welcome-badge">
        <EastIcon fontSize="small" />
        <span>Data Migration Wizard</span>
      </div>
      <h1 className="welcome-headline">
        Switching software<br />doesn't have to be hard.
      </h1>
      <p className="welcome-subtext">
        In just a few steps, we'll import your existing data — templates, clients, agents, and legacy inspection history. No manual entry. No data left behind.
      </p>

      <div className="welcome-steps-grid">
        {DATA_TYPE_ORDER.map((key) => {
          const cfg = DATA_TYPES[key];
          return (
            <div key={key} className="welcome-data-card" style={{ borderTop: `3px solid ${cfg.color}` }}>
              <div className="welcome-data-icon" style={{ background: cfg.colorBg, color: cfg.color }}>
                {cfg.icon}
              </div>
              <div>
                <Typography variant="subtitle2" fontWeight={700}>{cfg.label}</Typography>
                <Typography variant="caption" color="text.secondary">{cfg.description}</Typography>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        variant="contained"
        size="large"
        className="welcome-cta-btn"
        onClick={onStart}
        endIcon={<ArrowForwardIcon />}
      >
        Start My Migration
      </Button>

      <p className="welcome-note">Takes about 5 minutes. You can skip any section.</p>

      <div className="welcome-help-link">
        {/* <OpenInNewIcon fontSize="inherit" /> */}
        {/* <a
          href="https://support.inspectionsupport.com/hc/en-us/articles/how-to-export-data"
          target="_blank"
          rel="noopener noreferrer"
        > */}
          How to export your data from your current inspection software
        {/* </a> */}
      </div>
    </div>
  );
}

// ── ReviewStep ────────────────────────────────────────────────────────────────

function ReviewStep({ uploads, userDetails }) {
  const totalRecords = DATA_TYPE_ORDER.reduce((sum, key) => sum + effectiveRows(uploads[key]).length, 0);

  return (
    <div className="wizard-step-content review-step">
      <Typography variant="h6" className="review-title">Review your data</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Here's a summary of what will be imported into ISN. You can go back to make changes before submitting.
      </Typography>

      {userDetails?.name && (
        <div className="review-user-banner">
          <div className="review-user-info">
            <Typography variant="caption" className="review-user-label">Submitted by</Typography>
            <Typography variant="subtitle2" fontWeight={700}>{userDetails.name}</Typography>
            <Typography variant="caption" color="text.secondary">{userDetails.company}{userDetails.accountCode ? ` · Key: ${userDetails.accountCode}` : ""}</Typography>
          </div>
          <div className="review-user-contact">
            {userDetails.email && <Typography variant="caption" color="text.secondary">{userDetails.email}</Typography>}
            {userDetails.phone && <Typography variant="caption" color="text.secondary">{userDetails.phone}</Typography>}
          </div>
        </div>
      )}

      <div className="review-cards">
        {DATA_TYPE_ORDER.map((key) => {
          const cfg = DATA_TYPES[key];
          const file = uploads[key];
          const rows = effectiveRows(file);
          const isMapped = file?.mappedRows != null;
          return (
            <div key={key} className="review-card">
              <div className="review-card-icon" style={{ background: cfg.colorBg, color: cfg.color }}>
                {cfg.icon}
              </div>
              <div className="review-card-info">
                <Typography variant="subtitle2" fontWeight={700}>{cfg.label}</Typography>
                {file ? (
                  <>
                    <Typography variant="caption" color="text.secondary">{file.file.name}</Typography>
                    <div className="review-card-chips">
                      <Chip
                        label={`${rows.length} records`}
                        size="small"
                        sx={{ mt: 0.5, background: cfg.colorBg, color: cfg.color, fontWeight: 600 }}
                      />
                      {isMapped && (
                        <Chip
                          label="Mapped"
                          size="small"
                          sx={{ mt: 0.5, ml: 0.5, background: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }}
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary" fontStyle="italic">Skipped</Typography>
                )}
              </div>
              {file && (
                <div className="review-card-actions">
                  <CheckCircleOutlineIcon className="review-check" sx={{ color: cfg.color }} />
                  {file.rows?.length > 0 && (
                    <button
                      className="review-download-btn"
                      onClick={() => {
                        const downloadRows = file.mappedRows
                          ?? (cfg.importFields.length === 0
                            ? file.rows
                            : applyMapping(file.rows, buildInitialMapping(file.headers, cfg, file.rows), cfg));
                        generateISNFile(downloadRows, cfg.importFields, cfg.label, cfg);
                      }}
                      title={`Download ISN-format ${cfg.label} file`}
                    >
                      <DownloadOutlinedIcon fontSize="small" />
                      Download
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}

// ── SuccessStep ───────────────────────────────────────────────────────────────

function SuccessStep({ uploads, userDetails }) {
  const totalRecords = DATA_TYPE_ORDER.reduce((sum, key) => sum + effectiveRows(uploads[key]).length, 0);
  const uploadedItems = DATA_TYPE_ORDER.filter((k) => uploads[k]);

  return (
    <div className="success-step">
      <div className="success-icon-ring">
        <CheckIcon sx={{ fontSize: 48, color: "#fff" }} />
      </div>
      <Typography variant="h5" fontWeight={800} className="success-headline">
        You're all set!
      </Typography>
      <Typography variant="body1" color="text.secondary" className="success-subtext">
        {totalRecords > 0
          ? `${totalRecords.toLocaleString()} records have been submitted for import. Our team will process your data and notify you once it's ready.`
          : "Your import request has been submitted. Our team will reach out to get you set up."}
      </Typography>

      {uploadedItems.length > 0 && (
        <div className="success-checklist">
          {uploadedItems.map((key) => {
          const cfg = DATA_TYPES[key];
          const file = uploads[key];
          const rows = effectiveRows(file);
          const canDownload = file.mappedRows != null || (cfg.importFields.length === 0 && rows.length > 0);
          return (
            <div key={key} className="success-check-row">
              <CheckCircleOutlineIcon sx={{ color: cfg.color, mr: 1, flexShrink: 0 }} />
              <div className="success-check-info">
                <Typography variant="body2">
                  <strong>{cfg.label}</strong> — {rows.length} records
                </Typography>
                {canDownload && (
                  <button
                    className="success-download-link"
                    onClick={() => generateISNFile(file.mappedRows ?? file.rows, cfg.importFields, cfg.label, cfg)}
                  >
                    <DownloadOutlinedIcon fontSize="inherit" />
                    Download ISN-Import-{cfg.label}.xlsx
                  </button>
                )}
              </div>
            </div>
          );
        })}
        </div>
      )}

      {userDetails?.email && (
        <Typography variant="caption" color="text.secondary" display="block" mt={3}>
          We'll follow up with <strong>{userDetails.email}</strong> once your data has been processed.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        Questions? Contact our support team at <strong>support@palmtech.com</strong>
      </Typography>
    </div>
  );
}

// ── DataImport (root) ─────────────────────────────────────────────────────────

export default function DataImport() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Data Migration Wizard";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const [phase, setPhase] = useState("welcome"); // welcome | userdetails | wizard | success
  const [activeStep, setActiveStep] = useState(0);
  const [userDetails, setUserDetails] = useState({ name: "", company: "", accountCode: "", email: "", phone: "" });
  const [uploads, setUploads] = useState({
    templates: null,
    clients: null,
    agents: null,
    history: null,
  });
  const [columnMappings, setColumnMappings] = useState({
    templates: {},
    clients: {},
    agents: {},
    history: {},
  });
  const [submitting, setSubmitting] = useState(false);

  const currentKey = DATA_TYPE_ORDER[activeStep];

  const handleFileUpload = useCallback((data) => {
    setUploads((prev) => ({ ...prev, [currentKey]: data }));
    // Auto-build initial mapping from autoSuggest patterns
    const config = DATA_TYPES[currentKey];
    if (config.autoSuggest && data.headers?.length) {
      const initialMapping = buildInitialMapping(data.headers, config, data.rows ?? []);
      setColumnMappings((prev) => ({ ...prev, [currentKey]: initialMapping }));
    }
  }, [currentKey]);

  const handleFileClear = useCallback(() => {
    setUploads((prev) => ({ ...prev, [currentKey]: null }));
    setColumnMappings((prev) => ({ ...prev, [currentKey]: {} }));
  }, [currentKey]);

  const handleMappingChange = useCallback((newMapping) => {
    setColumnMappings((prev) => ({ ...prev, [currentKey]: newMapping }));
  }, [currentKey]);

  // mappedRows = null resets back to mapper; array = confirms mapping
  const handleMappingConfirm = useCallback((mappedRows) => {
    if (mappedRows != null) {
      const config = DATA_TYPES[currentKey];
      const missing = getMissingRequiredImportFields(columnMappings[currentKey], config);
      if (missing.length > 0) {
        toast.error(`Required field${missing.length > 1 ? "s" : ""} must be mapped: ${missing.join(", ")}`);
        return;
      }
    }
    setUploads((prev) => ({
      ...prev,
      [currentKey]: { ...prev[currentKey], mappedRows },
    }));
  }, [currentKey, columnMappings]);

  const handleNext = () => {
    const upload = uploads[currentKey];
    const config = DATA_TYPES[currentKey];
    if (upload && !upload.error && config.importFields.length > 0) {
      if (upload.mappedRows == null) {
        toast.error("Please confirm your column mapping before continuing.");
        return;
      }
      const missing = getMissingRequiredImportFields(columnMappings[currentKey], config);
      if (missing.length > 0) {
        toast.error(`Required field${missing.length > 1 ? "s" : ""} must be mapped: ${missing.join(", ")}`);
        return;
      }
    }
    if (activeStep < WIZARD_STEPS.length - 1) {
      setActiveStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((s) => s - 1);
    else setPhase("userdetails");
  };

  const handleSubmit = async () => {
    const apiKey = getSpectoraImportApiKey();
    if (!apiKey) {
      toast.error("Import service is unavailable. Please try again later.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      const companyKey = userDetails.accountCode?.trim() || null;
      formData.append("full_name", userDetails.name.trim());
      formData.append("company_name", userDetails.company.trim());
      formData.append("company_key", companyKey);
      formData.append("email", userDetails.email.trim());
      formData.append("phone_number", String(userDetails.phone ?? "").replace(/\D/g, ""));

      DATA_TYPE_ORDER.forEach((key) => {
        const file = buildSubmissionFile(uploads[key], key);
        if (file) formData.append(UPLOAD_FORM_FIELDS[key], file);
      });

      const response = await axios.post(API_CONSTANTS.SPECTORA_IMPORT_REQUEST, formData, {
        headers: { "x-api-key": apiKey },
      });

      if (response.data?.success) {
        setPhase("success");
      } else {
        toast.error(response.data?.message || "Failed to submit import request.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit import request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isReviewStep = activeStep === WIZARD_STEPS.length - 1;
  const hasAnyUpload = DATA_TYPE_ORDER.some((k) => uploads[k]);
  const uploadProgress = (DATA_TYPE_ORDER.filter((k) => uploads[k]).length / DATA_TYPE_ORDER.length) * 100;
  const canContinueCurrentStep = isUploadReadyToContinue(
    uploads[currentKey],
    columnMappings[currentKey],
    currentKey
  );

  if (phase === "success") {
    return (
      <div className="data-import-page">
        <div className="data-import-container">
          <SuccessStep uploads={uploads} userDetails={userDetails} />
        </div>
      </div>
    );
  }

  if (phase === "userdetails") {
    return (
      <div className="data-import-page">
        <div className="data-import-container">
          <UserDetailsStep
            userDetails={userDetails}
            onChange={setUserDetails}
            onBack={() => setPhase("welcome")}
            onContinue={() => setPhase("wizard")}
          />
        </div>
      </div>
    );
  }

  if (phase === "welcome") {
    return (
      <div className="data-import-page">
        <div className="data-import-container">
          <WelcomeStep onStart={() => setPhase("userdetails")} />
        </div>
      </div>
    );
  }

  return (
    <div className="data-import-page">
      <div className="data-import-container">
        {/* Header */}
        <div className="wizard-header">
          <div className="wizard-header-brand">
            <img
              src="https://cdn.builder.io/api/v1/image/assets%2F9b667fe848dd420d92676f1bc60bc542%2F93fd7f1513404b1b8592edbcab89def1?format=webp&width=800&height=1200"
              alt="ISN Logo"
              className="wizard-logo-img"
            />
            <Typography variant="caption" className="wizard-brand-label">Data Migration</Typography>
          </div>
          <div className="wizard-progress-wrap">
            <Typography variant="caption" color="text.secondary">
              {DATA_TYPE_ORDER.filter((k) => uploads[k]).length} of {DATA_TYPE_ORDER.length} files uploaded
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              className="wizard-progress-bar"
            />
          </div>
        </div>

        {/* Stepper */}
        <div className="stepper-wrap">
          <Stepper activeStep={activeStep} alternativeLabel>
            {WIZARD_STEPS.map((label, i) => (
              <Step key={label} completed={i < activeStep}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>

        {/* Step content */}
        <Paper className="wizard-panel" elevation={0}>
          {isReviewStep ? (
            <ReviewStep uploads={uploads} userDetails={userDetails} />
          ) : (
            <UploadStep
              dataType={currentKey}
              uploadedFile={uploads[currentKey]}
              onFileUpload={handleFileUpload}
              onFileClear={handleFileClear}
              mapping={columnMappings[currentKey]}
              onMappingChange={handleMappingChange}
              onMappingConfirm={handleMappingConfirm}
            />
          )}

          {/* Navigation */}
          <div className="wizard-nav">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              className="wizard-nav-back"
              variant="text"
            >
              Back
            </Button>

            <div className="wizard-nav-right">
              {!isReviewStep && (
                <Button
                  variant="text"
                  onClick={handleNext}
                  className="wizard-nav-skip"
                >
                  Skip {DATA_TYPES[currentKey]?.label}
                </Button>
              )}

              {isReviewStep ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="wizard-nav-submit"
                  endIcon={submitting ? null : <RocketLaunchOutlinedIcon />}
                >
                  {submitting ? "Submitting…" : "Submit Import Request"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIcon />}
                  className="wizard-nav-next"
                  disabled={Boolean(uploads[currentKey] && !canContinueCurrentStep)}
                >
                  {uploads[currentKey] ? "Continue" : "Next"}
                </Button>
              )}
            </div>
          </div>

          {submitting && <LinearProgress className="submit-progress" />}
        </Paper>
      </div>
    </div>
  );
}